import { KeyboardEventHandler, useEffect, useState } from 'react';
import { Key } from 'record-it-utils/utils/Key/Key';
import { useEventCallback } from '../../hooks/useEventCallback';

export function useHighlightedSelectOption<T>({
  options,
  currentOption,
  active,
  onOptionSelected
}: UseHighlightedSelectOptionOptions<T>): UseHighlightedSelectReturnValue<T> {
  const [highlightedOption, setHighlightedOption] = useState<T | null>(null);

  useEffect(() => {
    if (active) {
      setHighlightedOption(currentOption);
    }
  }, [currentOption, active, setHighlightedOption]);

  const highlightAtIndex = (index: number): void => {
    setHighlightedOption(options[index] ?? null);
  };

  const getCurrentIndex = (): number => {
    return highlightedOption ? options.indexOf(highlightedOption) : -1;
  };

  const highlightPreviousOption = (): void => {
    const previousIndex = getCurrentIndex() - 1;

    if (previousIndex >= 0) {
      highlightAtIndex(previousIndex);
    } else {
      highlightAtIndex(options.length - 1);
    }
  };

  const highlightNextOption = (): void => {
    const nextIndex = getCurrentIndex() + 1;

    if (nextIndex < options.length) {
      highlightAtIndex(nextIndex);
    } else {
      highlightAtIndex(0);
    }
  };

  const handleInputKeydown = useEventCallback<KeyboardEventHandler>((event) => {
    if (!active) {
      return;
    }

    switch (event.key) {
      case Key.ArrowUp:
        highlightPreviousOption();
        event.preventDefault();
        break;

      case Key.ArrowDown:
        highlightNextOption();
        event.preventDefault();
        break;

      case Key.Enter:
        if (highlightedOption) {
          onOptionSelected(highlightedOption);
        }
        break;
    }
  });

  return { highlightedOption, handleInputKeydown };
}

export type UseHighlightedSelectOptionOptions<T> = {
  options: Array<T>;
  currentOption: T;
  active: boolean;
  onOptionSelected: (option: T) => void;
};

export type UseHighlightedSelectReturnValue<T> = {
  highlightedOption: T | null;
  handleInputKeydown: KeyboardEventHandler;
};
